<mat-toolbar class="content-header">
    <mat-icon class="header-icon" fontIcon="person"></mat-icon>
    <h1 class="page-title" data-testid="edit-user-title">{{'users.edit_user' | translate}}</h1>
    <span class="toolbar-spacer"></span>
</mat-toolbar>

<div class="splitted-container">
    <ng-container>
        <div class="user-form">
            <form #userForm="ngForm">
                <!-- Étape 1 : Connexion -->
                <h2 class="form-step-title">1. {{'users.connection_info' | translate}}</h2>
                <div class="form-step">
                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.email'|translate}}</mat-label>
                        <input matInput required placeholder="{{'form.email'|translate}}" [(ngModel)]="user.email"
                            name="email" autocomplete="new-email" #emailInput="ngModel">
                        <mat-hint class="field-error"
                            *ngIf="emailInput.touched && user.email && !isEmailValid(user.email)">
                            {{'user.invalid_email'|translate}}
                        </mat-hint>
                    </mat-form-field>

                    <div class="input password-field">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>{{'form.password'|translate}}</mat-label>
                            <input matInput placeholder="{{'form.password'|translate}}"
                                [type]="passwordVisible ? 'text' : 'password'" [(ngModel)]="user.password" name="password"
                                autocomplete="new-password">
                            <div matSuffix class="password-actions">
                                <button mat-icon-button type="button" (click)="passwordVisible = !passwordVisible"
                                    [matTooltip]="(passwordVisible ? 'users.hide_password' : 'users.show_password') | translate">
                                    <mat-icon>{{passwordVisible ? 'visibility_off' : 'visibility'}}</mat-icon>
                                </button>
                                <button mat-icon-button type="button" (click)="generatePassword()"
                                    [matTooltip]="'users.generate_password' | translate" data-testid="generate-password">
                                    <mat-icon>casino</mat-icon>
                                </button>
                            </div>
                        </mat-form-field>

                        <p *ngIf="user.password && passwordMissingRules.length" class="password-hint"
                            data-testid="password-hint">
                            {{'users.password_missing' | translate}}
                            <ng-container *ngFor="let rule of passwordMissingRules; let last = last">
                                {{'users.password_missing_' + rule | translate: { min: passwordMinLength } }}{{last ? '' : ','}}
                            </ng-container>
                        </p>
                    </div>
                </div>

                <!-- Étape 2 : Informations personnelles -->

                <h2 class="form-step-title">2. {{'users.personal_info' | translate}}</h2>
                <div class="form-step">
                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.firstName'|translate}}</mat-label>
                        <input matInput required placeholder="{{'form.firstName'|translate}}"
                            [(ngModel)]="user.firstName" name="firstName">
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.lastName'|translate}}</mat-label>
                        <input matInput required placeholder="{{'form.lastName'|translate}}" [(ngModel)]="user.lastName"
                            name="lastName">
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.job'|translate}}</mat-label>
                        <input matInput required placeholder="{{'form.job'|translate}}" [(ngModel)]="user.job"
                            name="job">
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.company'|translate}}</mat-label>
                        <input matInput required placeholder="{{'form.company'|translate}}" [(ngModel)]="user.company"
                            name="company">
                    </mat-form-field>
                </div>

                <!-- Étape 3 : Paramètres -->
                <h2 class="form-step-title">3. {{'users.settings' | translate}}</h2>
                <div class="form-step">
                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'language.language'|translate}}</mat-label>
                        <mat-select required [(ngModel)]="user.language" name="language">
                            <mat-option value="fr-FR">{{'language.french'|translate}}</mat-option>
                            <mat-option value="en-US">{{'language.english'|translate}}</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.role'|translate}}</mat-label>
                        <mat-select required [(ngModel)]="selectedRole" name="role">
                            <mat-option *ngFor="let role of assignableRoles" [value]="role.name">{{role.name}}<span
                                    *ngIf="role.name == 'admin' || role.name == 'dev'">&nbsp;(CAD.42 user only)</span></mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="input" subscriptSizing="dynamic">
                        <mat-label>{{'form.reports_subscription'|translate}}</mat-label>
                        <mat-select [(ngModel)]="user.settings['subscribeToReport']" name="subscribeToReport">
                            <mat-option value="never">{{'form.never'|translate}}</mat-option>
                            <mat-option value="weekly">{{'form.weekly'|translate}}</mat-option>
                            <mat-option value="daily">{{'form.daily'|translate}}</mat-option>
                            <mat-option value="all">{{'form.report_all'|translate}}</mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>

                <!-- Étape 4 : Sécurité -->
                <h2 class="form-step-title">4. {{'users.security' | translate}}</h2>

                <div class="auth-methods">
                    <p class="auth-methods-label">{{'users.auth_methods' | translate}}</p>
                    <p class="auth-methods-hint">{{'users.auth_methods_hint' | translate}}</p>
                    <p class="auth-methods-hint locked" *ngIf="!canEditAuthMethods()">
                        <mat-icon fontIcon="lock" class="lock-icon"></mat-icon>
                        {{'users.auth_methods_locked' | translate}}
                    </p>

                    <div class="auth-methods-grid">
                        <button type="button" class="method-card" *ngFor="let method of authMethods"
                            [class.selected]="isAuthMethodAllowed(method.value)"
                            [disabled]="!canEditAuthMethods()"
                            (click)="toggleAuthMethod(method.value)">
                            <mat-icon class="method-check" fontIcon="check_circle"
                                *ngIf="isAuthMethodAllowed(method.value)">
                            </mat-icon>

                            <mat-icon *ngIf="method.icon" class="method-icon" [fontIcon]="method.icon"></mat-icon>
                            <img *ngIf="method.logo" class="method-icon" [src]="method.logo" alt="">

                            <span class="method-label">{{ method.label | translate }}</span>
                        </button>
                    </div>
                </div>

                <div class="form-step toggles">
                    <div class="toggle-field" *ngIf="isAuthMethodAllowed('password')">
                        <mat-slide-toggle [(ngModel)]="user.mfaEnabled" name="mfaEnabled" color="primary">
                            {{'users.mfa_enabled' | translate}}
                        </mat-slide-toggle>
                    </div>

                    <p class="auth-methods-hint" *ngIf="!isAuthMethodAllowed('password')">
                        <mat-icon fontIcon="info" class="lock-icon"></mat-icon>
                        {{'users.mfa_handled_by_sso' | translate}}
                    </p>
                </div>

                <div class="btn-actions">
                    <lib-button color="outline" icon="close" *ngIf="!loading" (click)="close()"
                        text="{{'form.close'|translate}}">
                    </lib-button>

                    <lib-button color="secondary" icon="mail" *ngIf="user.termsApproved == false && !loading && user.id"
                        (click)="resendInvitation()" text="{{'users.resend_invitation'|translate}}">
                    </lib-button>

                    <lib-button color="primary" class="update" (click)="updateProfile()" *ngIf="!loading"
                        [disabled]="!canSave()"
                        text="{{'form.save'|translate}}" icon="save">
                    </lib-button>

                    <lib-button color="primary" *ngIf="loading" icon="autorenew" text="{{'status.loading'|translate}}">
                    </lib-button>
                </div>
            </form>
        </div>

        <div class="project-list">
            <div class="option-menu">
                <div class="title">
                    <mat-icon class="header-icon" fontIcon="assignment"></mat-icon>
                    <h2>{{'users.assigned_projects'|translate}}</h2>
                </div>

                <app-search-bar (searchText)="search($event)"></app-search-bar>
            </div>

            <div class="chips" *ngIf="user.projects?.length">
                <mat-chip color="primary" *ngFor="let project of user.projects">
                    {{ project.name }}
                </mat-chip>
            </div>

            <div class="table-container">
                <table mat-table [dataSource]="projectsData" matSort>
                    <ng-container matColumnDef="name">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>
                            <div class="header-content">
                                <mat-icon fontIcon="person"></mat-icon>
                                <span>Name</span>
                            </div>
                        </th>

                        <td mat-cell *matCellDef="let row">
                            {{row.name}}
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="database">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>
                            <div class="header-content">
                                <mat-icon fontIcon="storage"></mat-icon>
                                <span>Database</span>
                            </div>
                        </th>
                        <td mat-cell *matCellDef="let row"> {{row.database}} </td>
                    </ng-container>

                    <ng-container matColumnDef="accesses">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header>
                            <div class="header-content">
                                <mat-icon fontIcon="lock"></mat-icon>
                                <span>{{'users.access_rights'|translate}}</span>
                            </div>
                        </th>

                        <td mat-cell *matCellDef="let row" class="access">
                            <mat-form-field appearance="outline" class="input" *ngIf="row.selected"
                                subscriptSizing="dynamic">
                                <mat-label>{{'users.access_rights'|translate}}</mat-label>

                                <mat-select [(ngModel)]="row.access" (selectionChange)="onAccessChange($event, row)">
                                    <mat-option [value]="false" [disabled]="!row.selected">
                                        {{ 'form.default' | translate }}
                                    </mat-option>

                                    <mat-option *ngFor="let access of row.accesses" [value]="access.id"
                                        [disabled]="!row.selected">
                                        {{access.name}}
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>

                            <div *ngIf="!row.selected">{{'form.default'|translate}}</div>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef>
                            <div class="header-content">
                                <mat-icon fontIcon="settings"></mat-icon>
                                <span>Actions</span>
                            </div>
                        </th>

                        <td mat-cell *matCellDef="let row">
                            <mat-checkbox [(ngModel)]="row.selected" (change)="onProjectSelected(row)"
                                [color]="row.archived ? archivedColor : undefined">
                            </mat-checkbox>
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="projectsColumns"></tr>
                    <tr mat-row *matRowDef="let row; columns: projectsColumns;"></tr>

                    <tr class="mat-row no-data-row" *matNoDataRow>
                        <td class="mat-cell" [attr.colspan]="projectsColumns.length">
                            <lib-no-data message="form.search_no_data" description="form.search_no_data_desc"
                                icon="search_off">
                            </lib-no-data>
                        </td>
                    </tr>
                </table>

                <mat-paginator #projectPaginator [length]="projectsCount" [pageSize]="10"
                    [pageSizeOptions]="[10, 25, 100]" class="paginator" (page)="handlePageEvent($event)">
                </mat-paginator>

                <div *ngIf="loading" class="table-overlay">
                    <app-spinner></app-spinner>
                </div>
            </div>
        </div>
    </ng-container>
</div>